import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  NzTableComponent,
  NzTableModule,
  NzTableQueryParams,
} from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { ProactiveStrategyComponent } from './proactive-strategy/proactive-strategy.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { SearchSelectComponent } from '../../../../../components/search-select/search-select.component';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ManageStudentDrawerParams } from '../../../../../features/insights/insights.interface';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ManageBehaviorStrategiesService } from './manage-behavior-strategies.service';
import { BehaviorStrategyItem, StrategyItem } from './strategy.interface';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { CreateOwnStrategyComponent } from './create-own-strategy/create-own-strategy.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SettingsService } from '../../../../../features/manage-organization/settings/settings.service';
import { TableQueryService } from '../../../../../core/services/table-query.service';
import { TableQueryParams } from '../../../../../core/models/table.models';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentInfoCardComponent } from '../../../../../components/student-info-card/student-info-card.component';
import { BehaviorInfoCardComponent } from '../../../../../components/behavior-info-card/behavior-info-card.component';
import { VideoModalComponent } from '../../../../../components/video-modal/video-modal.component';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';

@Component({
  selector: 'app-manage-behavior-strategies',
  standalone: true,
  imports: [
    NzFormModule,
    SvgIconComponent,
    NzDividerModule,
    NzSelectModule,
    NzRadioModule,
    NzButtonModule,
    NzTableModule,
    NzInputModule,
    NzModalModule,
    NzCheckboxModule,
    SearchSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NzRadioModule,
    NzSpinModule,
    StudentInfoCardComponent,
    BehaviorInfoCardComponent,
    VideoModalComponent,
    DrawerHeaderComponent,
    UserDatePipe,
  ],
  templateUrl: './manage-behavior-strategies.component.html',
  styleUrl: './manage-behavior-strategies.component.scss',
})
export class ManageBehaviorStrategiesComponent implements OnInit, OnDestroy {
  @ViewChild('rowSelectionTable')
  rowSelectionTable!: NzTableComponent<StrategyItem>;

  @ViewChild('publishStrategies')
  publishStrategies!: NzTableComponent<StrategyItem>;

  @ViewChild('drawerBehaviorStrategies')
  drawerBehaviorStrategies!: TemplateRef<void>;

  @ViewChild('drawerBehaviorCreate')
  drawerBehaviorCreate!: TemplateRef<void>;

  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly tableQueryService = inject(TableQueryService);
  private readonly settingsService = inject(SettingsService);
  private readonly fb = inject(FormBuilder);

  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  mode: 'view' | 'edit' | 'create' = 'view';

  caseParams!: ManageStudentDrawerParams;
  showGuideMe = false;
  showCreateOwn = false;
  selectedOption = 'Guide me';
  selectedId: number | null = null;
  isVisible = false;
  options: number[] = [];
  selectedIds: number[] = [];
  videoUrl = '';
  isVideoVisible = false;
  selectedIdType = '';
  isPrevDisabled = true;

  defaultParams: TableQueryParams = {
    pageIndex: 1,
    pageSize: 10,
    sortField: '',
    sortOrder: 0,
  };

  readonly strategiesForm = this.fb.group({
    behaviorStrategy: [null, [Validators.required]],
    type: [null, [Validators.required]],
    video: [null, [Validators.required]],
  });

  readonly publishStrategies$ =
    this.strategyService.publishStrategiesSubject.pipe(
      map(response => response?.result?.items ?? [])
    );

  pubCount$ = this.strategyService.publishStrategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  strategies$ = this.strategyService.strategiesSubject.pipe(
    map(response => response?.result?.items ?? [])
  );

  stratCount$ = this.strategyService.strategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  readonly behaviorStrategies$ = this.strategyService.behaviorStrategies$;

  totalRows$ = this.strategyService.totalRows$;

  flattenedStrategies$ = this.behaviorStrategies$.pipe(
    map((items: BehaviorStrategyItem[]) => items?.[0]?.behaviorStrategies || [])
  );

  constructor(
    private readonly loaderService: LoaderService,
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.loading$ = this.loaderService.loading$;
    this.route.queryParams.subscribe(params => {
      this.mode = params['mode'] || 'view';
      if (params['mode']) {
        this.onCancelled();
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }
    });
  }

  ngOnInit(): void {
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
    if (this.caseParams.orgId) {
      this.settingsService
        .getOptions(this.caseParams.orgId)
        .pipe(finalize(() => this.loaderService.hide()))
        .subscribe({
          next: data => {
            this.options = data.result.optionIds;
          },
          error: error => {
            console.error('Error fetching options:', error);
          },
        });
    }
  }

  closeManageBehaviorStrategiesDrawer() {
    this.drawerService.closeDrawer(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
  }

  closeCreateBehaviorStrategiesDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get behavior() {
    return this.caseParams.behavior;
  }

  get isTypeSelected(): boolean {
    return !!this.strategiesForm.get('type')?.value;
  }

  onCheckboxChange(id: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.push(id);
    } else {
      this.selectedIds = this.selectedIds.filter(item => item !== id);
    }
  }

  handleOptionChange(value: string): void {
    this.selectedOption = value;
  }

  handleNext(): void {
    const hasSelection = this.selectedId || this.selectedIds?.length;
    const isMultiSelect = !this.options.includes(5);

    if (this.selectedOption === 'Guide me') {
      this.showGuideMe = true;
      this.showCreateOwn = false;

      if (hasSelection && isMultiSelect) {
        this.showModal();
        return;
      }

      if (this.showGuideMe && this.selectedId && !this.showCreateOwn) {
        const strategy =
          (this.rowSelectionTable?.data as StrategyItem[])?.find(
            item => item.id === this.selectedId
          ) ?? null;

        this.openStrategyDrawer(strategy, 'edit', 'Guide me');
        this.selectedId = null;
      }
      return;
    }

    if (this.selectedOption === 'Create my own') {
      const shouldCreateNew =
        (this.options.includes(5) && !this.selectedId) ||
        (!this.options.includes(5) && !this.selectedIds?.length);

      if (shouldCreateNew) {
        this.showGuideMe = false;
        this.showCreateOwn = true;
        this.openStrategyDrawer(null, 'create');
        return;
      }

      if (hasSelection && isMultiSelect) {
        this.showModal();
        return;
      }

      if (this.selectedId) {
        const strategy =
          (this.publishStrategies?.data as StrategyItem[])?.find(
            item => item.id === this.selectedId
          ) ?? null;

        this.openStrategyDrawer(strategy, 'edit', 'Create my own');
        this.selectedId = null;
      }
    }
  }

  private openStrategyDrawer(
    strategy: StrategyItem | null,
    mode: 'edit' | 'create',
    from?: string
  ): void {
    this.drawerService.openDrawer(
      CreateOwnStrategyComponent,
      this.drawerBehaviorCreate as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        strategy,
        behaviorId: this.caseParams?.behavior?.id,
        mode,
        from,
        strategyTypeId: this.strategiesForm.value.type ?? undefined,
        caseParams: this.caseParams,
      },
      DRAWER_IDS.CREATE_OWN_STRATEGY
    );
  }

  handleClose() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES);
  }

  private resetState(): void {
    this.showGuideMe = false;
    this.showCreateOwn = false;
    // this.selectedOption = '';
    this.selectedId = null;
    this.selectedIds = [];
  }

  handleOk(): void {
    this.isVisible = false;
    this.mode = 'view';
    this.resetState();
    this.strategiesForm.get('type')?.setValue(null);
  }

  handleCancel(): void {
    this.isVisible = false;
  }

  handleCancelStep(): void {
    this.resetState();
    this.isPrevDisabled = true;
    this.mode = 'create';
  }

  onCancelled(): void {
    this.mode = 'view';
    this.resetState();
  }

  setSelectedId(id: number, type: string): void {
    if (type === 'strategies') {
      this.selectedId = id;
      this.selectedIdType = type;
    } else if (type === 'publishStrategies') {
      this.selectedId = id;
      this.selectedIdType = type;
    }
  }

  showModal(): void {
    if (this.options.includes(5) && !this.selectedId) return;
    if (!this.options.includes(5) && !this.selectedIds?.length) return;

    this.isVisible = true;
    const strategyIds = this.options.includes(5)
      ? [this.selectedId]
      : this.selectedIds;

    this.onStrategySelect(strategyIds.filter(Boolean) as number[]);
  }

  openVideoUrl(url: string): void {
    this.isVideoVisible = true;
    this.videoUrl = url;
  }

  onStrategySelect(id: number[]): void {
    const caseBehaviorId = this.caseParams?.behavior?.id;
    if (!caseBehaviorId) return;
    this.loaderService.show();
    this.handleStrategyCreation(id, caseBehaviorId);
  }

  openProactiveStrategyDrawer(data: StrategyItem): void {
    this.drawerService.openDrawer(
      ProactiveStrategyComponent,
      this.drawerBehaviorStrategies as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        data,
        mode: this.mode,
        behaviorId: this.caseParams?.behavior?.id,
        caseParams: this.caseParams,
      },
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { type } = this.strategiesForm.value;
    if (this.mode === 'view') {
      const queryParams = this.tableQueryService.buildQueryParams(
        params,
        this.defaultParams
      );

      this.loaderService.show();

      this.strategyService
        .getBehaviorStrategyList({
          caseBehaviorId: this.caseParams?.behavior?.id,
          strategyType: type ?? undefined,
          ...queryParams,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.loaderService.hide())
        )
        .subscribe({
          next: () => {
            this.defaultParams.pageIndex = params.pageIndex;
            this.defaultParams.pageSize = params.pageSize;
          },
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    } else {
      this.isPrevDisabled = false;
      const queryParams = this.tableQueryService.buildQueryParams(
        params,
        this.defaultParams
      );

      this.loaderService.show();

      this.strategyService
        .getStrategyList({
          caseBehaviorId: this.caseParams?.behavior?.id,
          strategyType: type ?? undefined,
          ...queryParams,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.loaderService.hide())
        )
        .subscribe({
          next: () => {
            this.defaultParams.pageIndex = params.pageIndex;
            this.defaultParams.pageSize = params.pageSize;
          },
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    }
  }

  onPublishStrategyQueryParamsChange(params: NzTableQueryParams): void {
    if (this.selectedOption !== 'Create my own') return;

    const queryParams = this.tableQueryService.buildQueryParams(
      params,
      this.defaultParams
    );

    this.loaderService.show();

    this.strategyService
      .getPublishStrategyList({
        organizationId: this.caseParams.orgId ?? 0,
        caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
        ...queryParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.defaultParams.pageIndex = params.pageIndex;
          this.defaultParams.pageSize = params.pageSize;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.mode = params.mode;
        }
        if (this.mode === 'edit') {
          this.showGuideMe = false;
          this.showCreateOwn = true;
        }
      });
  }

  private handleStrategyCreation(id: number[], caseBehaviorId: number): void {
    const { type } = this.strategiesForm.value;
    this.strategyService
      .createStrategy({ caseBehaviorId, behaviorStrategyIds: id })
      .pipe(
        tap(() => {
          this.commonService.notify(
            'success',
            'Behavior strategy added successfully'
          );
          if (this.selectedOption === 'Guide me') {
            this.strategyService
              .getStrategyList({
                caseBehaviorId: this.caseParams?.behavior?.id,
                strategyType: type ?? undefined,
                ...this.defaultParams,
              })
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                  this.loaderService.hide();
                  this.selectedId = null;
                  this.selectedIds = [];
                })
              )
              .subscribe({
                next: () => {
                  this.defaultParams.pageIndex = 1;
                  this.defaultParams.pageSize = 10;
                  this.selectedId = null;
                  this.selectedIds = [];
                },
                error: ({ errors, message }: ErrorResponse) => {
                  const errorMessages = errors.map(
                    err => err.description || err.detailedMessage
                  );
                  this.commonService.notify('error', errorMessages, message);
                },
              });
          } else {
            this.strategyService
              .getPublishStrategyList({
                organizationId: this.caseParams.orgId ?? 0,
                caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
                ...this.defaultParams,
              })
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loaderService.hide())
              )
              .subscribe({
                next: () => {
                  this.defaultParams.pageIndex = 1;
                  this.defaultParams.pageSize = 10;
                  this.selectedId = null;
                },
                error: ({ errors, message }: ErrorResponse) => {
                  const errorMessages = errors.map(
                    err => err.description || err.detailedMessage
                  );
                  this.commonService.notify('error', errorMessages, message);
                },
              });
          }
        }),
        catchError(({ errors, message }: ErrorResponse) => {
          this.commonService.notify(
            'error',
            errors?.map(err => err.description || err.detailedMessage) || [
              'An error occurred while creating the strategy.',
            ],
            message
          );
          this.selectedId = null;
          return EMPTY;
        }),
        finalize(() => {
          this.loaderService.hide();
          this.selectedId = null;
          this.selectedIds = [];
        })
      )
      .subscribe();
  }

  getStrategyTypeText(): string {
    const selectedType = this.strategiesForm.get('type')?.value;

    const strategyTexts: Record<number, string> = {
      71: 'Replacement Behavior strategies',
      72: 'Proactive strategies',
      73: 'Reactive strategies',
    };

    return selectedType ? strategyTexts[selectedType] : 'strategies';
  }

  getStatusText(status: number): string {
    const statusTexts: Record<number, string> = {
      1: 'Active',
      2: 'Inactive',
    };
    return statusTexts[status] || '';
  }

  openEditOwnStrategyComponent(data: StrategyItem): void {
    this.drawerService.openDrawer(
      CreateOwnStrategyComponent,
      'Edit Behavior Strategy',
      1170,
      true,
      undefined,
      {
        strategy: data,
        behaviorId: this.caseParams?.behavior?.id,
        mode: 'edit',
        caseParams: this.caseParams,
      },
      DRAWER_IDS.CREATE_OWN_STRATEGY
    );
  }
}
